/**
 * Central Route Configuration - Single Source of Truth
 * All navigation paths must use this constant to ensure type-safety and consistency
 */

export const ROUTES = {
  // Public routes - No auth required
  public: {
    landing: '/',
    auth: '/auth',
    forgotPassword: '/forgot-password',
    terms: '/terms',
    privacy: '/privacy',
  },
  
  // Approval flow routes
  approval: {
    pending: '/pending-approval',
    denied: '/access-denied',
    success: '/approval-success',
    permissionDenied: '/permission-denied',
  },
  
  // Main app routes - Require auth + restaurant
  app: {
    dashboard: '/dashboard',
    inventory: '/inventory',
    stock: '/stock',
    categories: '/inventory/categories',
    menu: '/menu',
    costs: '/costs',
    sales: {
      root: '/sales',
      alacarte: '/sales/alacarte',
      rodizio: '/sales/rodizio',
      weight: '/sales/weight',
    },
    reports: {
      root: '/reports',
      dre: '/reports?tab=dre',
      balanceSheet: '/reports?tab=balance-sheet',
      menuEngineering: '/reports/menu-engineering',
      priceReview: '/reports/price-review',
      cmvDiagnostic: '/reports/cmv-diagnostic',
    },
    settings: '/settings',
    subscription: '/settings?tab=subscription',
    checkout: '/checkout',
    addonCheckout: '/addon-checkout',
    checkoutSuccess: '/checkout-success',
    organization: '/organization',
  },
  
  // Onboarding routes
  onboarding: {
    wizard: '/onboarding-wizard',
  },
  
  // Internal support routes
  support: {
    base: '/internal-support-alc2024',
    login: '/internal-support-alc2024/login',
    dashboard: '/internal-support-alc2024/dashboard',
    clients: '/internal-support-alc2024/clients',
    metrics: '/internal-support-alc2024/metrics',
    notifications: '/internal-support-alc2024/notifications',
    tickets: '/internal-support-alc2024/tickets',
    auditLogs: '/internal-support-alc2024/audit-logs',
    approvals: '/internal-support-alc2024/approvals',
    coupons: '/internal-support-alc2024/coupons',
    referrals: '/internal-support-alc2024/referrals',
    retentionOffers: '/internal-support-alc2024/retention-offers',
  },
} as const;

// Type-safe route helpers
export type PublicRoute = typeof ROUTES.public[keyof typeof ROUTES.public];
export type ApprovalRoute = typeof ROUTES.approval[keyof typeof ROUTES.approval];
export type SalesRoute = typeof ROUTES.app.sales[keyof typeof ROUTES.app.sales];
export type ReportsRoute = typeof ROUTES.app.reports[keyof typeof ROUTES.app.reports];

// Service type to sales route mapping
export const SERVICE_TYPE_SALES_ROUTES: Record<string, string[]> = {
  // À La Carte based services
  alacarte: [ROUTES.app.sales.alacarte],
  pizzaria: [ROUTES.app.sales.alacarte],
  hamburgueria: [ROUTES.app.sales.alacarte],
  lanchonete: [ROUTES.app.sales.alacarte],
  cafeteria: [ROUTES.app.sales.alacarte],
  sorveteria: [ROUTES.app.sales.alacarte],
  doceria: [ROUTES.app.sales.alacarte],
  padaria: [ROUTES.app.sales.alacarte],
  bar: [ROUTES.app.sales.alacarte],
  restaurante: [ROUTES.app.sales.alacarte],
  
  // Rodizio based services
  rodizio: [ROUTES.app.sales.rodizio],
  churrascaria: [ROUTES.app.sales.rodizio],
  
  // Weight/Self-service based
  por_peso: [ROUTES.app.sales.weight],
  self_service: [ROUTES.app.sales.weight],
  buffet: [ROUTES.app.sales.weight],
  
  // Mixed services (show all)
  misto: [ROUTES.app.sales.alacarte, ROUTES.app.sales.rodizio, ROUTES.app.sales.weight],
};

// Get default sales route for a service type
export const getDefaultSalesRoute = (serviceType: string | null | undefined): string => {
  if (!serviceType) return ROUTES.app.sales.alacarte;
  
  const routes = SERVICE_TYPE_SALES_ROUTES[serviceType.toLowerCase()];
  return routes?.[0] ?? ROUTES.app.sales.alacarte;
};

// Get available sales routes for a service type
// NOTA: Retorna TODAS as modalidades para máxima flexibilidade operacional
export const getAvailableSalesRoutes = (serviceType: string | null | undefined): string[] => {
  // Sempre retornar todas as rotas de vendas
  // O usuário decide qual modalidade usar com base em sua operação
  return [ROUTES.app.sales.alacarte, ROUTES.app.sales.rodizio, ROUTES.app.sales.weight];
};
